import { useRef, useState } from "react";
import { MobileLayout } from "../../components/MobileLayout";
import { useApp } from "../../context/AppContext";
import {
  CheckCircle2,
  AlertTriangle,
  Camera,
  ShieldCheck,
  ListChecks,
  Save,
  Clock,
  ImagePlus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { appConfig, formatHora } from "../../data/mock";

export default function ChecklistPage() {
  const { checklistPosto, user, postos, condominios, getById } = useApp();

  const posto = getById(postos, user?.postoId);
  const condominio = getById(condominios, user?.condominioId);

  const [checklist, setChecklist] = useState(
    checklistPosto.map((item) => ({
      ...item,
      status: "pendente",
      observacao: "",
      foto: null,
      fotoNome: "",
    })),
  );

  const [checklistSalvo, setChecklistSalvo] = useState(null);

  const total = checklist.length;
  const ok = checklist.filter((item) => item.status === "ok").length;
  const pendencias = checklist.filter((item) => item.status === "pendencia").length;
  const pendentes = checklist.filter((item) => item.status === "pendente").length;
  const progresso = total > 0 ? Math.round(((ok + pendencias) / total) * 100) : 0;

  const atualizarItem = (id, campo, valor) => {
    setChecklist((atual) =>
      atual.map((item) =>
        item.id === id
          ? {
              ...item,
              [campo]: valor,
            }
          : item,
      ),
    );
  };

  const salvarChecklist = () => {
    const pendenciasSemObservacao = checklist.filter(
      (item) => item.status === "pendencia" && !item.observacao.trim(),
    );

    if (pendenciasSemObservacao.length > 0) {
      toast.error("Revise as pendências", {
        description: "Todo item com pendência precisa ter uma observação.",
      });
      return;
    }

    if (pendentes > 0) {
      toast.error("Checklist incompleto", {
        description: "Marque todos os itens como OK ou Pendência.",
      });
      return;
    }

    const registro = {
      id: `checklist-${Date.now()}`,
      criadoEm: new Date().toISOString(),
      posto: posto?.nome || "Posto não informado",
      condominio: condominio?.nome || appConfig.condominioPadrao,
      ok,
      pendencias,
      itens: checklist,
    };

    setChecklistSalvo(registro);

    toast.success("Checklist salvo", {
      description: "Checklist demonstrativo registrado com sucesso.",
    });
  };

  const resetarChecklist = () => {
    setChecklist(
      checklistPosto.map((item) => ({
        ...item,
        status: "pendente",
        observacao: "",
        foto: null,
        fotoNome: "",
      })),
    );

    setChecklistSalvo(null);

    toast.info("Checklist reiniciado");
  };

  return (
    <MobileLayout title="Checklist do Posto" scrollable>
      {/* Cabeçalho */}
      <div className="rounded-2xl bg-gradient-to-br from-[#0d1b3e] to-[#0a1128] border border-white/10 p-5 mb-4">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="tiny-label text-blue-200">Conferência operacional</p>
            <h2 className="font-display font-bold text-lg mt-1">
              Checklist do posto
            </h2>

            <p className="text-xs text-slate-400 mt-1">
              {posto?.nome || "Posto não informado"} ·{" "}
              <span translate="no">
                {condominio?.nome || appConfig.condominioPadrao}
              </span>
            </p>
          </div>

          <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
            <ListChecks className="w-5 h-5 text-blue-300" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <MiniResumo icon={CheckCircle2} label="OK" value={ok} />
          <MiniResumo icon={AlertTriangle} label="Pendências" value={pendencias} />
          <MiniResumo icon={Clock} label="Pendentes" value={pendentes} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="tiny-label text-slate-400">Progresso</span>

            <span className="font-mono-data text-sm text-slate-300">
              {ok + pendencias} / {total}
            </span>
          </div>

          <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${progresso}%` }}
            />
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="space-y-3 mb-5">
        {checklist.map((item) => (
          <ChecklistItem
            key={item.id}
            item={item}
            onStatusChange={(status) => atualizarItem(item.id, "status", status)}
            onObsChange={(valor) => atualizarItem(item.id, "observacao", valor)}
            onFotoChange={(foto, fotoNome) => {
              atualizarItem(item.id, "foto", foto);
              atualizarItem(item.id, "fotoNome", fotoNome);
            }}
            onRemoveFoto={() => {
              atualizarItem(item.id, "foto", null);
              atualizarItem(item.id, "fotoNome", "");
            }}
          />
        ))}
      </div>

      {/* Botões */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <button
          type="button"
          onClick={resetarChecklist}
          className="py-3.5 rounded-xl bg-white/5 border border-white/10 text-slate-300 font-semibold"
        >
          Reiniciar
        </button>

        <button
          type="button"
          onClick={salvarChecklist}
          className="flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-500 text-black font-semibold"
        >
          <Save className="w-4 h-4" />
          Salvar
        </button>
      </div>

      {/* Status salvo */}
      {checklistSalvo && (
        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-4 mb-5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-300 mt-0.5" />

            <div>
              <p className="text-sm font-semibold text-emerald-100">
                Checklist salvo
              </p>

              <p className="text-xs text-emerald-100/70 mt-1">
                Registrado às {formatHora(checklistSalvo.criadoEm)} ·{" "}
                {checklistSalvo.ok} item(ns) OK · {checklistSalvo.pendencias}{" "}
                pendência(s)
              </p>
            </div>
          </div>
        </div>
      )}

    </MobileLayout>
  );
}

const ChecklistItem = ({
  item,
  onStatusChange,
  onObsChange,
  onFotoChange,
  onRemoveFoto,
}) => {
  const fileInputRef = useRef(null);

  const ok = item.status === "ok";
  const pendencia = item.status === "pendencia";

  const handleFoto = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      onFotoChange(reader.result, file.name);

      toast.success("Foto anexada", {
        description: "Imagem adicionada à pendência.",
      });
    };

    reader.readAsDataURL(file);

    event.target.value = "";
  };

  return (
    <div
      data-testid={`check-${item.id}`}
      className={`rounded-2xl border p-4 transition-colors ${
        ok
          ? "bg-emerald-500/10 border-emerald-500/30"
          : pendencia
            ? "bg-amber-500/10 border-amber-500/30"
            : "bg-[#0a1128] border-white/10"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              ok
                ? "bg-emerald-500/20 text-emerald-300"
                : pendencia
                  ? "bg-amber-500/20 text-amber-300"
                  : "bg-white/5 text-slate-500"
            }`}
          >
            {ok ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : pendencia ? (
              <AlertTriangle className="w-5 h-5" />
            ) : (
              <ListChecks className="w-5 h-5" />
            )}
          </div>

          <div className="min-w-0">
            <p className="text-sm font-semibold leading-snug text-slate-100">
              {item.item}
            </p>

            {item.obrigatorio && (
              <p className="text-[11px] text-amber-400 mt-0.5">
                Obrigatório
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onStatusChange("ok")}
          className={`py-2.5 rounded-xl text-xs font-semibold border ${
            ok
              ? "bg-emerald-500 text-black border-emerald-500"
              : "bg-white/5 text-slate-300 border-white/10"
          }`}
        >
          OK
        </button>

        <button
          type="button"
          onClick={() => onStatusChange("pendencia")}
          className={`py-2.5 rounded-xl text-xs font-semibold border ${
            pendencia
              ? "bg-amber-500 text-black border-amber-500"
              : "bg-white/5 text-slate-300 border-white/10"
          }`}
        >
          Pendência
        </button>
      </div>

      {pendencia && (
        <div className="mt-3 space-y-2">
          <textarea
            value={item.observacao}
            onChange={(e) => onObsChange(e.target.value)}
            rows={2}
            placeholder="Descreva a pendência encontrada..."
            className="w-full px-3 py-2 rounded-lg bg-[#040914] border border-white/10 text-xs focus:outline-none focus:border-blue-500/50 resize-none"
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFoto}
            className="hidden"
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300"
          >
            <Camera className="w-3.5 h-3.5" />
            {item.foto ? "Trocar foto" : "Abrir câmera / anexar foto"}
          </button>

          {item.foto && (
            <div className="rounded-xl bg-[#040914] border border-white/10 overflow-hidden">
              <img
                src={item.foto}
                alt="Foto da pendência"
                className="w-full max-h-44 object-cover"
              />

              <div className="p-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <ImagePlus className="w-4 h-4 text-emerald-300 shrink-0" />
                  <p className="text-xs text-slate-400 truncate">
                    {item.fotoNome || "Foto anexada"}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onRemoveFoto}
                  className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-slate-400"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MiniResumo = ({ icon: Icon, label, value }) => {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
      <Icon className="w-4 h-4 text-blue-300 mb-2" />
      <p className="text-lg font-bold text-white">{value}</p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
};